import {
	LoginRequestDto,
	LoginSSORequestDto,
	LogoutReason,
	LogoutRequestDto,
	LogoutUserDto,
} from "@api-types/user/LoginRequestDto";
import {
	LoginResponseDto,
	LoginUserMatchingResponseDto,
	RedirectLoginResponseDto,
} from "@api-types/user/LoginResponseDto";
import { UserMatchingRequestDto } from "@api-types/user/UserMatchingRequestDto";
import { LoginErrorResponseModel } from "../../../data/types";

import {
	UserTypes,
	getAgencyId,
	getAgencyKey,
	getSecondaryUser,
	getShopId,
	getUserToken,
	isTypeOf,
} from "@utils";
import { LoginType } from "@utils/common/loginTypeEnum";
import { MitAuthDto } from "@api-types/integrations/MitAuthDto";
import { BankIdAuthDto } from "@api-types/integrations";
import UserApiService from "@api-services/user/UserApiService";
import AgencyUserAccountRelationApiService from "@api-services/user/AgencyUserAccountRelationApiService";
import { errorCodesMap } from "./ErrorCodesMap";
import {
	validateAcceptGdprConsentResponse,
	validateMatchingDataResponse,
	validateRefreshUserResponse,
	validateUserLoginResponse,
	validateUserMatchConfirmationResponse,
} from "@utils/auth";
import { logger } from "@utils/logger/logger";
import UserDataApiService from "@api-services/user/UserDataApiService";

export default class UserService {
	static async loginUser(
		userData:
			| LoginRequestDto
			| LoginSSORequestDto
			| MitAuthDto
			| BankIdAuthDto,
		loginType: LoginType = LoginType.DEFAULT
	): Promise<LoginResponseDto | null> {
		try {
			let response: LoginResponseDto | RedirectLoginResponseDto;

			switch (loginType) {
				case LoginType.MITID:
					response = await UserApiService.loginUserWithMit(
						userData as MitAuthDto
					);
					break;
				case LoginType.BANKID:
					response = await UserApiService.loginUserWithBankId(
						userData as BankIdAuthDto
					);
					break;
				case LoginType.SSO:
					response = await UserApiService.ssoLogin(
						userData as LoginSSORequestDto
					);
					break;
				default:
					response = await UserApiService.loginUser(
						userData as LoginRequestDto
					);
			}

			const isResponseValid = validateUserLoginResponse(response);

			if (!isResponseValid) {
				return null;
			}

			if (isTypeOf<RedirectLoginResponseDto>(response, "redirectUrl")) {
				window.location.replace(response.redirectUrl);
				return null;
			}

			return response;
		} catch (error) {
			if (error == null || error?.message == "Unauthorized") {
				throw new Error("USER_UNABLE");
			}

			const errorCode = Number(error?.data?.errorCode);

			const errorInfo = errorCodesMap(error).get(errorCode) || {
				errorMessage: "USER_UNABLE",
			};

			const modelError: LoginErrorResponseModel = {
				errorCode: error?.data?.errorCode,
				message: errorInfo.errorMessage,
				mobileApkLink: errorInfo.mobileApkLink,
				errorFailedAttempts: errorInfo.errorFailedAttempts,
				errorDuration: errorInfo.errorDuration,
				userData: errorInfo.userData,
			};

			throw new LoginErrorResponseModel(modelError);
		}
	}

	static async findMatchingData(): Promise<string[]> {
		try {
			const response =
				await AgencyUserAccountRelationApiService.findMatchingData();

			const isResponseValid = validateMatchingDataResponse(response);

			return isResponseValid ? response : [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async confirmUserMatching(
		userData: UserMatchingRequestDto
	): Promise<LoginUserMatchingResponseDto> {
		try {
			const response = await UserApiService.confirmUserMatching(userData);

			const isResponseValid =
				validateUserMatchConfirmationResponse(response);

			if (!isResponseValid) {
				throw new Error("Response not valid");
			}
			return response;
		} catch (error) {
			const modelError: LoginErrorResponseModel = {
				errorCode: error?.data?.errorCode,
			};
			throw new LoginErrorResponseModel(modelError);
		}
	}

	static async denyUserMatching(): Promise<void> {
		try {
			return await AgencyUserAccountRelationApiService.denyUserMatching();
		} catch (error) {
			const modelError: LoginErrorResponseModel = {
				errorCode: error?.data?.errorCode,
			};

			await App.state.rootStore.userAuthStore.logoutUser();

			throw new LoginErrorResponseModel(modelError);
		}
	}

	static async deactivateUser(
		isFromRealityCheck: boolean = false
	): Promise<void> {
		try {
			return await UserDataApiService.deactivateUser(isFromRealityCheck);
		} catch (error) {
			logger.logError(error);
		}
	}

	static async logoutUser(reasonsObj?: LogoutReason): Promise<void> {
		let requestModel = {} as LogoutRequestDto;
		requestModel.primary = {} as LogoutUserDto;
		const primaryToken = getUserToken();
		requestModel.primary.token = primaryToken ? primaryToken.token : "";
		requestModel.primary.agencyId = getAgencyId();
		requestModel.primary.agencyKey = getAgencyKey();
		requestModel.primary.shopId = getShopId();

		if (getSecondaryUser()) {
			requestModel.secondary = {} as LogoutUserDto;
			const secondaryToken = getUserToken(UserTypes.SECONDARY);
			requestModel.secondary.token = secondaryToken
				? secondaryToken.token
				: "";
			requestModel.secondary.agencyId = getAgencyId(UserTypes.SECONDARY);
			requestModel.secondary.agencyKey = getAgencyKey(
				UserTypes.SECONDARY
			);
			requestModel.secondary.shopId = getShopId(UserTypes.SECONDARY);
		}
		if (reasonsObj) {
			requestModel.primary.isAutomaticLogout =
				reasonsObj.isAutomaticLogout;
			requestModel.primary.isRealityCheckLogout =
				reasonsObj.isRealityCheckLogout;
		}

		try {
			await UserApiService.logoutUser(requestModel);
		} catch (error) {
			logger.logError(error);
		}
	}

	static async acceptGDPRConsent() {
		try {
			const response = await UserApiService.acceptGDPRConsent();

			const isResponseValid = validateAcceptGdprConsentResponse(response);

			if (!isResponseValid) {
				throw new Error("Response is not valid");
			}
			return response;
		} catch (error) {
			logger.logError(error);
			return false;
		}
	}

	static async refreshUser(userId: string, isPrimary: boolean) {
		try {
			const response = await UserApiService.refreshUser(
				userId,
				isPrimary
			);

			const isResponseValid = validateRefreshUserResponse(response);

			if (!isResponseValid) {
				throw new Error("Response is not valid");
			}
			return response;
		} catch (error) {
			logger.logError(error);
			return null;
		}
	}
}
